import { useCallback, useEffect, useState } from 'react';
import { excelToolExecutor } from '../services/excel/ExcelToolExecutor';
import { ensureWorkbookId } from '../services/excel/workbookIdentity';
import type { AgentContext } from '../types/agent';

/** The selection is context only when the user added it (product owner, 2026-09-10: off by default, a chip
 *  toggles it); otherwise the snapshot goes without it. Pure, for the tests. */
export function applySelection(context: AgentContext, include: boolean): AgentContext {
  return include ? context : { ...context, selection: null };
}

/** Builds the per-turn context (#11, §10.1) cheaply: sheet list + active sheet + current selection (when the user
 *  opted in, `includeSelection`). The workbook id is the pane's current one (`getWorkbookId`) — the one the index
 *  runs under, which an identity answer may have changed since the pane opened; it is created on the first
 *  message when there is none yet (never on open). */
export function useWorkbookContext(getWorkbookId: () => string | null, onWorkbookId?: (id: string) => void, includeSelection?: () => boolean) {
  const build = useCallback(async (): Promise<AgentContext> => {
    let id = getWorkbookId();
    if (!id) {
      id = await ensureWorkbookId();
      onWorkbookId?.(id);
    }
    const locale = typeof navigator !== 'undefined' ? navigator.language : 'en-US';
    const context = await excelToolExecutor.buildContext(id, locale);
    return applySelection(context, includeSelection ? includeSelection() : true);
  }, [getWorkbookId, onWorkbookId, includeSelection]);

  return { build };
}

export interface WorkbookInfo {
  name: string | null;
  activeSheet: string | null;
}

/** Workbook name + active sheet for the welcome heading; refreshed when the selection moves
 *  (which is when the active sheet can change). Never throws: falls back to nulls. */
export function useWorkbookInfo(selection: { sheet: string; address: string } | null): WorkbookInfo {
  const [info, setInfo] = useState<WorkbookInfo>({ name: null, activeSheet: null });
  useEffect(() => {
    let cancelled = false;
    excelToolExecutor
      .readWorkbookInfo()
      .then((i) => {
        if (!cancelled) setInfo({ name: i.name, activeSheet: i.activeSheet });
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [selection?.sheet]);
  return info;
}
